import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X, Image as ImageIcon, ExternalLink, LogOut, Upload, MapPin, Building2, FileText, CheckCircle, Play, RotateCcw, Hash, Settings } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [clients, setClients] = useState([]);
  const [homepageServices, setHomepageServices] = useState([]);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingCredential, setEditingCredential] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [activeTab, setActiveTab] = useState('projects');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  
  const [projectForm, setProjectForm] = useState({
    title: '',
    client_name: '',
    location: '',
    status: 'Ongoing',
    scope: '',
    quantity: '',
    start_date: '',
    completion_date: '',
    industry_type: '',
    images: [],
    priority: 0,
    show_on_homepage: false,
  });

  const [credentialForm, setCredentialForm] = useState({
    title: '',
    company_name: '',
    location: '',
    work_description: '',
    executed_quantity: '',
    certificate_image: '',
    priority: 0,
  });

  const [clientForm, setClientForm] = useState({
    company_name: '',
    logo_url: '',
    priority_order: '',
  });

  const [imageUrl, setImageUrl] = useState('');
  const [credImageDragging, setCredImageDragging] = useState(false);
  const [clientLogoDragging, setClientLogoDragging] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  useEffect(() => {
    fetchProjects();
    fetchQuotations();
    fetchCredentials();
    fetchClients();
    fetchHomepageServices();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${API}/quotations`);
      setQuotations(response.data);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    }
  };

  const fetchCredentials = async () => {
    try {
      const response = await axios.get(`${API}/credentials`);
      setCredentials(response.data);
    } catch (error) {
      console.error('Error fetching credentials:', error);
    }
  };

  const handleCredentialSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...credentialForm,
        title: credentialForm.company_name,
      };
      if (editingCredential) {
        await axios.put(`${API}/credentials/${editingCredential.id}`, payload);
      } else {
        await axios.post(`${API}/credentials`, payload);
      }
      resetCredentialForm();
      fetchCredentials();
    } catch (error) {
      console.error('Error saving credential:', error);
      alert('Failed to save credential');
    }
  };

  const handleDeleteCredential = async (id) => {
    if (window.confirm('Are you sure you want to delete this credential?')) {
      try {
        await axios.delete(`${API}/credentials/${id}`);
        fetchCredentials();
      } catch (error) {
        console.error('Error deleting credential:', error);
      }
    }
  };

  const handleEditCredential = (credential) => {
    setCredentialForm({
      title: credential.title || '',
      company_name: credential.company_name || '',
      location: credential.location || '',
      work_description: credential.work_description || '',
      executed_quantity: credential.executed_quantity || '',
      certificate_image: credential.certificate_image || '',
      priority: credential.priority || 0,
    });
    setEditingCredential(credential);
    setShowCredentialForm(true);
  };

  const resetCredentialForm = () => {
    setCredentialForm({
      title: '',
      company_name: '',
      location: '',
      work_description: '',
      executed_quantity: '',
      certificate_image: '',
      priority: 0,
    });
    setEditingCredential(null);
    setShowCredentialForm(false);
  };

  const handleCredImageDrop = useCallback((e) => {
    e.preventDefault();
    setCredImageDragging(false);
    const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      setCredentialForm(prev => ({ ...prev, certificate_image: url.trim() }));
    }
  }, []);

  const handleClientLogoDrop = useCallback((e) => {
    e.preventDefault();
    setClientLogoDragging(false);
    const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      setClientForm(prev => ({ ...prev, logo_url: url.trim() }));
    }
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchHomepageServices = async () => {
    try {
      const response = await axios.get(`${API}/homepage-services`);
      setHomepageServices(response.data);
    } catch (error) {
      console.error('Error fetching homepage services:', error);
    }
  };

  const handleUpdateService = async (serviceId, field, value) => {
    try {
      await axios.put(`${API}/homepage-services/${serviceId}`, { [field]: value });
      fetchHomepageServices();
    } catch (error) {
      console.error('Error updating service:', error);
    }
  };

  const handleSaveService = async (serviceId, data) => {
    try {
      await axios.put(`${API}/homepage-services/${serviceId}`, data);
      fetchHomepageServices();
      alert('Service updated successfully!');
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service');
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...clientForm,
        priority_order: clientForm.priority_order ? parseInt(clientForm.priority_order) : null,
      };
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, payload);
      } else {
        await axios.post(`${API}/clients`, payload);
      }
      resetClientForm();
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Failed to save client');
    }
  };

  const handleDeleteClient = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await axios.delete(`${API}/clients/${id}`);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  const handleEditClient = (client) => {
    setClientForm({
      company_name: client.company_name || '',
      logo_url: client.logo_url || '',
      priority_order: client.priority_order ?? '',
    });
    setEditingClient(client);
    setShowClientForm(true);
  };

  const resetClientForm = () => {
    setClientForm({ company_name: '', logo_url: '', priority_order: '' });
    setEditingClient(null);
    setShowClientForm(false);
  };

  const handleStartProject = async (id) => {
    try {
      await axios.put(`${API}/projects/${id}/start`);
      fetchProjects();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start project');
    }
  };

  const handleCompleteProject = async (id) => {
    if (window.confirm('Mark this project as completed?')) {
      try {
        await axios.put(`${API}/projects/${id}/complete`);
        fetchProjects();
      } catch (error) {
        alert(error.response?.data?.detail || 'Failed to complete project');
      }
    }
  };

  const handleReopenProject = async (id) => {
    if (window.confirm('Move this project back to Ongoing?')) {
      try {
        await axios.put(`${API}/projects/${id}/reopen`);
        fetchProjects();
      } catch (error) {
        alert(error.response?.data?.detail || 'Failed to reopen project');
      }
    }
  };

  const handleUpdateRank = async (id, rank) => {
    try {
      await axios.put(`${API}/projects/${id}/rank`, { rank_order: parseInt(rank) || 0 });
      fetchProjects();
    } catch (error) {
      console.error('Error updating rank:', error);
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, projectForm);
      } else {
        await axios.post(`${API}/projects`, projectForm);
      }
      resetForm();
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project');
    }
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await axios.delete(`${API}/projects/${id}`);
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleEditProject = (project) => {
    setProjectForm(project);
    setEditingProject(project);
    setShowProjectForm(true);
  };

  const handleAddImage = () => {
    if (imageUrl.trim()) {
      setProjectForm({
        ...projectForm,
        images: [...projectForm.images, imageUrl.trim()]
      });
      setImageUrl('');
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = projectForm.images.filter((_, i) => i !== index);
    setProjectForm({ ...projectForm, images: updatedImages });
  };

  const resetForm = () => {
    setProjectForm({
      title: '',
      client_name: '',
      location: '',
      status: 'Ongoing',
      scope: '',
      quantity: '',
      start_date: '',
      completion_date: '',
      industry_type: '',
      images: [],
      priority: 0,
      show_on_homepage: false,
    });
    setImageUrl('');
    setEditingProject(null);
    setShowProjectForm(false);
  };

  const getFilteredProjects = () => {
    if (projectStatusFilter === 'all') return projects;
    return projects.filter(p => p.status === projectStatusFilter);
  };

  const getProjectCountByStatus = (status) => {
    return projects.filter(p => p.status === status).length;
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center" data-testid="admin-header">
          <div>
            <h1 className="font-oswald font-bold text-4xl text-slate-900 mb-2">ADMIN DASHBOARD</h1>
            <p className="text-slate-600">Manage projects and quotation requests</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 text-slate-700 hover:text-orange-500 border border-slate-300 hover:border-orange-500 transition-all"
            data-testid="logout-button"
          >
            <LogOut size={20} />
            <span className="font-oswald">LOGOUT</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('projects')}
            data-testid="tab-projects"
            className={`font-oswald pb-4 px-2 transition-colors ${
              activeTab === 'projects'
                ? 'text-orange-500 border-b-4 border-orange-500'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            PROJECTS ({projects.length})
          </button>
          <button
            onClick={() => setActiveTab('quotations')}
            data-testid="tab-quotations"
            className={`font-oswald pb-4 px-2 transition-colors ${
              activeTab === 'quotations'
                ? 'text-orange-500 border-b-4 border-orange-500'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            QUOTATIONS ({quotations.length})
          </button>
          <button
            onClick={() => setActiveTab('credentials')}
            data-testid="tab-credentials"
            className={`font-oswald pb-4 px-2 transition-colors ${
              activeTab === 'credentials'
                ? 'text-orange-500 border-b-4 border-orange-500'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            CREDENTIALS ({credentials.length})
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            data-testid="tab-clients"
            className={`font-oswald pb-4 px-2 transition-colors ${
              activeTab === 'clients'
                ? 'text-orange-500 border-b-4 border-orange-500'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            CLIENTS ({clients.length})
          </button>
          <button
            onClick={() => setActiveTab('homepage')}
            data-testid="tab-homepage"
            className={`font-oswald pb-4 px-2 transition-colors ${
              activeTab === 'homepage'
                ? 'text-orange-500 border-b-4 border-orange-500'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            HOMEPAGE SETTINGS
          </button>
        </div>

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div data-testid="projects-tab">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white p-6 border-l-4 border-blue-500 shadow-sm">
                <div className="text-3xl font-oswald font-bold text-blue-500">{getProjectCountByStatus('Ongoing')}</div>
                <div className="text-sm text-slate-600 mt-1">Ongoing Projects</div>
              </div>
              <div className="bg-white p-6 border-l-4 border-green-500 shadow-sm">
                <div className="text-3xl font-oswald font-bold text-green-500">{getProjectCountByStatus('Completed')}</div>
                <div className="text-sm text-slate-600 mt-1">Completed Projects</div>
              </div>
              <div className="bg-white p-6 border-l-4 border-yellow-500 shadow-sm">
                <div className="text-3xl font-oswald font-bold text-yellow-500">{getProjectCountByStatus('Upcoming')}</div>
                <div className="text-sm text-slate-600 mt-1">Upcoming Projects</div>
              </div>
              <div className="bg-white p-6 border-l-4 border-orange-500 shadow-sm">
                <div className="text-3xl font-oswald font-bold text-slate-900">{projects.length}</div>
                <div className="text-sm text-slate-600 mt-1">Total Projects</div>
              </div>
            </div>

            {/* Filter and Add Button */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setProjectStatusFilter('all')}
                  className={`px-4 py-2 font-oswald text-sm ${
                    projectStatusFilter === 'all'
                      ? 'bg-orange-500 text-white'
                      : 'bg-white text-slate-700 border border-slate-300'
                  }`}
                >
                  ALL
                </button>
                <button
                  onClick={() => setProjectStatusFilter('Ongoing')}
                  className={`px-4 py-2 font-oswald text-sm ${
                    projectStatusFilter === 'Ongoing'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-slate-700 border border-slate-300'
                  }`}
                >
                  ONGOING
                </button>
                <button
                  onClick={() => setProjectStatusFilter('Completed')}
                  className={`px-4 py-2 font-oswald text-sm ${
                    projectStatusFilter === 'Completed'
                      ? 'bg-green-500 text-white'
                      : 'bg-white text-slate-700 border border-slate-300'
                  }`}
                >
                  COMPLETED
                </button>
                <button
                  onClick={() => setProjectStatusFilter('Upcoming')}
                  className={`px-4 py-2 font-oswald text-sm ${
                    projectStatusFilter === 'Upcoming'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-white text-slate-700 border border-slate-300'
                  }`}
                >
                  UPCOMING
                </button>
              </div>

              <button
                onClick={() => setShowProjectForm(true)}
                data-testid="add-project-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white font-oswald px-6 py-3 inline-flex items-center space-x-2 transition-all"
              >
                <Plus size={20} />
                <span>ADD NEW PROJECT</span>
              </button>
            </div>

            {/* Project Form */}
            {showProjectForm && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-8 shadow-lg border-l-4 border-orange-500 mb-8"
                data-testid="project-form"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-oswald font-bold text-2xl text-slate-900">
                    {editingProject ? 'EDIT PROJECT' : 'ADD NEW PROJECT'}
                  </h2>
                  <button onClick={resetForm} data-testid="close-form-btn">
                    <X size={24} className="text-slate-500 hover:text-slate-900" />
                  </button>
                </div>

                <form onSubmit={handleProjectSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Project Title *</label>
                      <input
                        type="text"
                        value={projectForm.title}
                        onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                        data-testid="form-title"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Client Name *</label>
                      <input
                        type="text"
                        value={projectForm.client_name}
                        onChange={(e) => setProjectForm({ ...projectForm, client_name: e.target.value })}
                        data-testid="form-client"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Location *</label>
                      <input
                        type="text"
                        value={projectForm.location}
                        onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })}
                        data-testid="form-location"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Status *</label>
                      <select
                        value={projectForm.status}
                        onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
                        data-testid="form-status"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      >
                        <option value="Ongoing">Ongoing</option>
                        <option value="Completed">Completed</option>
                        <option value="Upcoming">Upcoming</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Quantity *</label>
                      <input
                        type="text"
                        value={projectForm.quantity}
                        onChange={(e) => setProjectForm({ ...projectForm, quantity: e.target.value })}
                        data-testid="form-quantity"
                        placeholder="e.g., 3000 MT"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Industry Type</label>
                      <input
                        type="text"
                        value={projectForm.industry_type}
                        onChange={(e) => setProjectForm({ ...projectForm, industry_type: e.target.value })}
                        data-testid="form-industry"
                        placeholder="e.g., Ferro Alloy"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Priority Order *</label>
                      <input
                        type="number"
                        value={projectForm.priority}
                        onChange={(e) => setProjectForm({ ...projectForm, priority: parseInt(e.target.value) || 0 })}
                        data-testid="form-priority"
                        placeholder="0 = highest priority"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      />
                      <p className="text-xs text-slate-500 mt-1">Lower number = higher priority (0 appears first)</p>
                    </div>
                    <div className="flex items-center space-x-3 p-4 bg-slate-50 border border-slate-300">
                      <input
                        type="checkbox"
                        checked={projectForm.show_on_homepage}
                        onChange={(e) => setProjectForm({ ...projectForm, show_on_homepage: e.target.checked })}
                        data-testid="form-homepage"
                        className="w-5 h-5 text-orange-500 focus:ring-orange-500"
                      />
                      <label className="text-sm font-medium text-slate-700">
                        Show on Homepage (Featured Project)
                      </label>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Scope of Work *</label>
                    <textarea
                      value={projectForm.scope}
                      onChange={(e) => setProjectForm({ ...projectForm, scope: e.target.value })}
                      data-testid="form-scope"
                      rows="4"
                      placeholder="Describe the project scope and work details..."
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 px-4 py-3"
                      required
                    />
                  </div>

                  {/* Image Upload Section */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <ImageIcon className="inline w-4 h-4 mr-1" />
                      Project Images
                    </label>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="url"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                        className="flex-1 bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      />
                      <button
                        type="button"
                        onClick={handleAddImage}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 font-oswald"
                      >
                        ADD IMAGE
                      </button>
                    </div>

                    {projectForm.images.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        {projectForm.images.map((img, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={img}
                              alt={`Project ${index + 1}`}
                              className="w-full h-32 object-cover border border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={16} />
                            </button>
                            <a
                              href={img}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute bottom-1 right-1 bg-slate-900 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <ExternalLink size={16} />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      data-testid="cancel-btn"
                      className="border-2 border-slate-300 text-slate-700 font-oswald px-6 py-3 hover:border-slate-400 transition-all"
                    >
                      CANCEL
                    </button>
                    <button
                      type="submit"
                      data-testid="save-project-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white font-oswald px-8 py-3 inline-flex items-center space-x-2 transition-all"
                    >
                      <Save size={20} />
                      <span>{editingProject ? 'UPDATE' : 'SAVE'} PROJECT</span>
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Projects List */}
            <div className="space-y-4">
              {getFilteredProjects().length === 0 ? (
                <div className="bg-white p-12 text-center text-slate-500">
                  No projects found. Click "Add New Project" to get started.
                </div>
              ) : (
                getFilteredProjects().map((project) => (
                  <div
                    key={project.id}
                    className={`bg-white p-6 shadow-sm hover:shadow-md transition-shadow border-l-4 ${
                      project.status === 'Completed'
                        ? 'border-green-500'
                        : project.status === 'Ongoing'
                        ? 'border-blue-500'
                        : 'border-yellow-500'
                    }`}
                    data-testid={`project-item-${project.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-oswald font-bold text-xl text-slate-900">{project.title}</h3>
                          <span
                            className={`px-3 py-1 text-xs font-oswald ${
                              project.status === 'Completed'
                                ? 'bg-green-100 text-green-800'
                                : project.status === 'Ongoing'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {project.status}
                          </span>
                          {project.status === 'Completed' && project.rank_order != null && (
                            <span className="px-2 py-1 text-xs font-oswald bg-slate-100 text-slate-600">
                              Rank #{project.rank_order}
                            </span>
                          )}
                        </div>
                        <p className="text-slate-700 mb-1"><strong>Client:</strong> {project.client_name}</p>
                        <p className="text-slate-700 mb-1"><strong>Location:</strong> {project.location}</p>
                        <p className="text-slate-700 mb-1"><strong>Quantity:</strong> {project.quantity}</p>
                        <p className="text-sm text-slate-600 mt-2">{project.scope}</p>
                        
                        {project.images && project.images.length > 0 && (
                          <div className="flex gap-2 mt-3">
                            {project.images.slice(0, 3).map((img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                alt={`${project.title} ${idx + 1}`}
                                className="w-20 h-20 object-cover border border-slate-200"
                              />
                            ))}
                            {project.images.length > 3 && (
                              <div className="w-20 h-20 bg-slate-100 flex items-center justify-center text-slate-600 text-sm">
                                +{project.images.length - 3} more
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col items-end gap-2 ml-4">
                        {/* Edit & Delete - always shown */}
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditProject(project)}
                            data-testid={`edit-project-${project.id}`}
                            className="p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <Edit size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteProject(project.id)}
                            data-testid={`delete-project-${project.id}`}
                            className="p-2 text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>

                        {/* Status-specific actions */}
                        {project.status === 'Ongoing' && (
                          <button
                            onClick={() => handleCompleteProject(project.id)}
                            data-testid={`complete-project-${project.id}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-oswald transition-colors"
                          >
                            <CheckCircle size={14} />
                            MARK COMPLETED
                          </button>
                        )}

                        {project.status === 'Upcoming' && (
                          <button
                            onClick={() => handleStartProject(project.id)}
                            data-testid={`start-project-${project.id}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-oswald transition-colors"
                          >
                            <Play size={14} />
                            START PROJECT
                          </button>
                        )}

                        {project.status === 'Completed' && (
                          <>
                            <button
                              onClick={() => handleReopenProject(project.id)}
                              data-testid={`reopen-project-${project.id}`}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-500 hover:bg-slate-600 text-white text-xs font-oswald transition-colors"
                            >
                              <RotateCcw size={14} />
                              MOVE TO ONGOING
                            </button>
                            <div className="flex items-center gap-1.5">
                              <Hash size={14} className="text-slate-400" />
                              <input
                                type="number"
                                min="1"
                                value={project.rank_order ?? ''}
                                onChange={(e) => handleUpdateRank(project.id, e.target.value)}
                                placeholder="Rank"
                                data-testid={`rank-project-${project.id}`}
                                className="w-16 h-8 text-xs text-center bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Quotations Tab */}
        {activeTab === 'quotations' && (
          <div data-testid="quotations-tab">
            <div className="space-y-4">
              {quotations.length === 0 ? (
                <div className="bg-white p-12 text-center text-slate-500">
                  No quotation requests yet.
                </div>
              ) : (
                quotations.map((quot) => (
                  <div
                    key={quot.id}
                    className="bg-white p-6 shadow-sm border-l-4 border-orange-500"
                    data-testid={`quotation-item-${quot.id}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="font-oswald font-bold text-xl text-slate-900 mb-3">{quot.company_name}</h3>
                        <p className="text-sm text-slate-700 mb-1"><strong>Contact:</strong> {quot.contact_person} ({quot.designation})</p>
                        <p className="text-sm text-slate-700 mb-1"><strong>Mobile:</strong> {quot.mobile}</p>
                        <p className="text-sm text-slate-700 mb-1"><strong>Email:</strong> {quot.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-700 mb-1"><strong>Project:</strong> {quot.project_name}</p>
                        <p className="text-sm text-slate-700 mb-1"><strong>Location:</strong> {quot.project_location}</p>
                        <p className="text-sm text-slate-700 mb-1"><strong>Industry:</strong> {quot.industry_type}</p>
                        <p className="text-sm text-slate-700 mb-1"><strong>Quantity:</strong> {quot.estimated_quantity} MT</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-xs text-slate-600 mb-2"><strong>Nature of Work:</strong></p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {quot.nature_of_work.map((work, idx) => (
                          <span key={idx} className="bg-orange-100 text-orange-800 px-2 py-1 text-xs">
                            {work}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-slate-600"><strong>Scope:</strong> {quot.scope_description}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Credentials Tab */}
        {activeTab === 'credentials' && (
          <div data-testid="credentials-tab">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white p-6 border-l-4 border-orange-500 shadow-sm">
                <div className="text-3xl font-oswald font-bold text-slate-900">{credentials.length}</div>
                <div className="text-sm text-slate-600 mt-1">Total Credentials</div>
              </div>
              <div className="bg-white p-6 border-l-4 border-green-500 shadow-sm">
                <div className="text-3xl font-oswald font-bold text-green-500">{credentials.filter(c => c.certificate_image).length}</div>
                <div className="text-sm text-slate-600 mt-1">With Images</div>
              </div>
            </div>

            {/* Add Button */}
            <div className="flex justify-end mb-6">
              <button
                onClick={() => { resetCredentialForm(); setShowCredentialForm(true); }}
                data-testid="add-credential-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white font-oswald px-6 py-3 inline-flex items-center space-x-2 transition-all"
              >
                <Plus size={20} />
                <span>ADD NEW CREDENTIAL</span>
              </button>
            </div>

            {/* Credential Form */}
            {showCredentialForm && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-8 shadow-lg border-l-4 border-orange-500 mb-8"
                data-testid="credential-form"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-oswald font-bold text-2xl text-slate-900">
                    {editingCredential ? 'EDIT CREDENTIAL' : 'ADD NEW CREDENTIAL'}
                  </h2>
                  <button onClick={resetCredentialForm} data-testid="close-credential-form-btn">
                    <X size={24} className="text-slate-500 hover:text-slate-900" />
                  </button>
                </div>

                <form onSubmit={handleCredentialSubmit}>
                  {/* Image Upload Section */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <ImageIcon className="inline w-4 h-4 mr-1" />
                      Credential Image
                    </label>

                    {/* Drop Zone */}
                    <div
                      onDragOver={(e) => { e.preventDefault(); setCredImageDragging(true); }}
                      onDragLeave={() => setCredImageDragging(false)}
                      onDrop={handleCredImageDrop}
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-3 ${
                        credImageDragging
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-300 bg-slate-50 hover:border-slate-400'
                      }`}
                      data-testid="credential-image-dropzone"
                    >
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Drag & drop an image URL here</p>
                      <p className="text-xs text-slate-400 mt-1">or paste the URL below</p>
                    </div>

                    <input
                      type="url"
                      value={credentialForm.certificate_image}
                      onChange={(e) => setCredentialForm({ ...credentialForm, certificate_image: e.target.value })}
                      placeholder="Enter image URL (e.g., https://example.com/certificate.jpg)"
                      data-testid="credential-image-url"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                    />

                    {credentialForm.certificate_image && (
                      <div className="mt-3 relative inline-block">
                        <img
                          src={credentialForm.certificate_image}
                          alt="Preview"
                          className="h-32 object-cover border border-slate-200"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <button
                          type="button"
                          onClick={() => setCredentialForm({ ...credentialForm, certificate_image: '' })}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company Name *</label>
                      <input
                        type="text"
                        value={credentialForm.company_name}
                        onChange={(e) => setCredentialForm({ ...credentialForm, company_name: e.target.value })}
                        data-testid="credential-company-name"
                        placeholder="e.g., Tata Steel Ltd."
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Job Location *</label>
                      <input
                        type="text"
                        value={credentialForm.location}
                        onChange={(e) => setCredentialForm({ ...credentialForm, location: e.target.value })}
                        data-testid="credential-location"
                        placeholder="e.g., Jamshedpur, Jharkhand"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Executed Quantity *</label>
                      <input
                        type="text"
                        value={credentialForm.executed_quantity}
                        onChange={(e) => setCredentialForm({ ...credentialForm, executed_quantity: e.target.value })}
                        data-testid="credential-quantity"
                        placeholder="e.g., 5000 MT"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Priority Order</label>
                      <input
                        type="number"
                        value={credentialForm.priority}
                        onChange={(e) => setCredentialForm({ ...credentialForm, priority: parseInt(e.target.value) || 0 })}
                        data-testid="credential-priority"
                        placeholder="0 = highest priority"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      />
                      <p className="text-xs text-slate-500 mt-1">Lower number = higher priority</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Work Description</label>
                    <textarea
                      value={credentialForm.work_description}
                      onChange={(e) => setCredentialForm({ ...credentialForm, work_description: e.target.value })}
                      data-testid="credential-work-description"
                      rows="3"
                      placeholder="Describe the work performed (optional)"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 px-4 py-3"
                    />
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={resetCredentialForm}
                      data-testid="cancel-credential-btn"
                      className="border-2 border-slate-300 text-slate-700 font-oswald px-6 py-3 hover:border-slate-400 transition-all"
                    >
                      CANCEL
                    </button>
                    <button
                      type="submit"
                      data-testid="save-credential-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white font-oswald px-8 py-3 inline-flex items-center space-x-2 transition-all"
                    >
                      <Save size={20} />
                      <span>{editingCredential ? 'UPDATE' : 'SAVE'} CREDENTIAL</span>
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Credentials List */}
            <div className="space-y-4" data-testid="credentials-list">
              {credentials.length === 0 ? (
                <div className="bg-white p-12 text-center text-slate-500">
                  No credentials found. Click "Add New Credential" to get started.
                </div>
              ) : (
                credentials.map((cred) => (
                  <div
                    key={cred.id}
                    className="bg-white p-6 shadow-sm border-l-4 border-orange-500 hover:shadow-md transition-shadow"
                    data-testid={`credential-item-${cred.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Thumbnail */}
                      {cred.certificate_image && (
                        <div className="w-24 h-24 flex-shrink-0 overflow-hidden border border-slate-200 bg-slate-100">
                          <img
                            src={cred.certificate_image}
                            alt={cred.company_name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-oswald font-bold text-xl text-slate-900 mb-1">{cred.company_name}</h3>
                        <div className="flex items-center text-sm text-slate-600 mb-1">
                          <MapPin size={14} className="mr-1 text-orange-500 flex-shrink-0" />
                          <span>{cred.location}</span>
                        </div>
                        {cred.executed_quantity && (
                          <p className="text-sm text-slate-600"><strong>Quantity:</strong> {cred.executed_quantity}</p>
                        )}
                        {cred.work_description && (
                          <p className="text-sm text-slate-500 mt-1 truncate">{cred.work_description}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-2 flex-shrink-0">
                        <button
                          onClick={() => handleEditCredential(cred)}
                          data-testid={`edit-credential-${cred.id}`}
                          className="p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <Edit size={20} />
                        </button>
                        <button
                          onClick={() => handleDeleteCredential(cred.id)}
                          data-testid={`delete-credential-${cred.id}`}
                          className="p-2 text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div data-testid="clients-tab">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white p-6 border-l-4 border-orange-500 shadow-sm">
                <div className="text-3xl font-oswald font-bold text-slate-900">{clients.length}</div>
                <div className="text-sm text-slate-600 mt-1">Total Clients</div>
              </div>
              <div className="bg-white p-6 border-l-4 border-blue-500 shadow-sm">
                <div className="text-3xl font-oswald font-bold text-blue-500">6</div>
                <div className="text-sm text-slate-600 mt-1">Displayed on Homepage</div>
              </div>
            </div>

            <div className="flex justify-end mb-6">
              <button
                onClick={() => { resetClientForm(); setShowClientForm(true); }}
                data-testid="add-client-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white font-oswald px-6 py-3 inline-flex items-center space-x-2 transition-all"
              >
                <Plus size={20} />
                <span>ADD NEW CLIENT</span>
              </button>
            </div>

            {showClientForm && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-8 shadow-lg border-l-4 border-orange-500 mb-8"
                data-testid="client-form"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-oswald font-bold text-2xl text-slate-900">
                    {editingClient ? 'EDIT CLIENT' : 'ADD NEW CLIENT'}
                  </h2>
                  <button onClick={resetClientForm} data-testid="close-client-form-btn">
                    <X size={24} className="text-slate-500 hover:text-slate-900" />
                  </button>
                </div>

                <form onSubmit={handleClientSubmit}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      <ImageIcon className="inline w-4 h-4 mr-1" />
                      Company Logo
                    </label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setClientLogoDragging(true); }}
                      onDragLeave={() => setClientLogoDragging(false)}
                      onDrop={handleClientLogoDrop}
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-3 ${
                        clientLogoDragging
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-300 bg-slate-50 hover:border-slate-400'
                      }`}
                      data-testid="client-logo-dropzone"
                    >
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Drag & drop a logo URL here</p>
                      <p className="text-xs text-slate-400 mt-1">or paste the URL below</p>
                    </div>
                    <input
                      type="url"
                      value={clientForm.logo_url}
                      onChange={(e) => setClientForm({ ...clientForm, logo_url: e.target.value })}
                      placeholder="Enter logo URL (e.g., https://example.com/logo.png)"
                      data-testid="client-logo-url"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                    />
                    {clientForm.logo_url && (
                      <div className="mt-3 relative inline-block">
                        <img
                          src={clientForm.logo_url}
                          alt="Logo Preview"
                          className="h-20 object-contain border border-slate-200 bg-white p-2"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <button
                          type="button"
                          onClick={() => setClientForm({ ...clientForm, logo_url: '' })}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company Name *</label>
                      <input
                        type="text"
                        value={clientForm.company_name}
                        onChange={(e) => setClientForm({ ...clientForm, company_name: e.target.value })}
                        data-testid="client-company-name"
                        placeholder="e.g., Jai Balaji Industries"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Priority Order</label>
                      <input
                        type="number"
                        min="1"
                        value={clientForm.priority_order}
                        onChange={(e) => setClientForm({ ...clientForm, priority_order: e.target.value })}
                        data-testid="client-priority"
                        placeholder="1 = highest priority"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      />
                      <p className="text-xs text-slate-500 mt-1">Top 6 clients by priority shown on Homepage</p>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={resetClientForm}
                      data-testid="cancel-client-btn"
                      className="border-2 border-slate-300 text-slate-700 font-oswald px-6 py-3 hover:border-slate-400 transition-all"
                    >
                      CANCEL
                    </button>
                    <button
                      type="submit"
                      data-testid="save-client-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white font-oswald px-8 py-3 inline-flex items-center space-x-2 transition-all"
                    >
                      <Save size={20} />
                      <span>{editingClient ? 'UPDATE' : 'SAVE'} CLIENT</span>
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            <div className="space-y-4" data-testid="clients-list">
              {clients.length === 0 ? (
                <div className="bg-white p-12 text-center text-slate-500">
                  No clients found. Click "Add New Client" to get started.
                </div>
              ) : (
                clients.map((client, index) => (
                  <div
                    key={client.id}
                    className="bg-white p-5 shadow-sm border-l-4 border-orange-500 hover:shadow-md transition-shadow"
                    data-testid={`client-item-${client.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {client.logo_url ? (
                        <div className="w-16 h-16 flex-shrink-0 border border-slate-200 bg-white p-1 flex items-center justify-center">
                          <img src={client.logo_url} alt={client.company_name} className="max-w-full max-h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 flex-shrink-0 border border-slate-200 bg-slate-100 flex items-center justify-center">
                          <Building2 size={24} className="text-slate-300" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h3 className="font-oswald font-bold text-lg text-slate-900">{client.company_name}</h3>
                        <p className="text-sm text-slate-500">
                          Priority: {client.priority_order ?? 'Not set'}
                          {index < 6 && <span className="ml-2 text-green-600 font-medium">Shown on Homepage</span>}
                        </p>
                      </div>

                      <div className="flex space-x-2 flex-shrink-0">
                        <button
                          onClick={() => handleEditClient(client)}
                          data-testid={`edit-client-${client.id}`}
                          className="p-2 text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <Edit size={20} />
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client.id)}
                          data-testid={`delete-client-${client.id}`}
                          className="p-2 text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Homepage Settings Tab */}
        {activeTab === 'homepage' && (
          <div data-testid="homepage-tab">
            <div className="bg-white p-6 border-l-4 border-orange-500 shadow-sm mb-8">
              <div className="flex items-center gap-3">
                <Settings size={24} className="text-orange-500" />
                <div>
                  <h2 className="font-oswald font-bold text-2xl text-slate-900">HOMEPAGE SERVICES</h2>
                  <p className="text-sm text-slate-600">Edit the four service cards shown on the homepage. Changes appear instantly.</p>
                </div>
              </div>
            </div>

            <div className="space-y-6" data-testid="homepage-services-list">
              {homepageServices.map((service, index) => (
                <ServiceEditor
                  key={service.id}
                  service={service}
                  index={index}
                  onSave={handleSaveService}
                />
              ))}
              {homepageServices.length === 0 && (
                <div className="bg-white p-12 text-center text-slate-500">
                  Loading homepage services...
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ServiceEditor = ({ service, index, onSave }) => {
  const [title, setTitle] = useState(service.title);
  const [description, setDescription] = useState(service.description);
  const [image, setImage] = useState(service.image || '');
  const [dragging, setDragging] = useState(false);
  const [changed, setChanged] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      setImage(url.trim());
      setChanged(true);
    }
  };

  const handleSave = () => {
    onSave(service.id, { title, description, image });
    setChanged(false);
  };

  return (
    <div
      className="bg-white p-6 shadow-sm border-l-4 border-orange-500"
      data-testid={`service-editor-${index}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="bg-orange-500 text-white w-8 h-8 flex items-center justify-center font-oswald font-bold text-sm">
          {index + 1}
        </span>
        <h3 className="font-oswald font-bold text-lg text-slate-900">{service.title}</h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Image */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Service Image</label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg overflow-hidden transition-colors ${
              dragging ? 'border-orange-500 bg-orange-50' : 'border-slate-300'
            }`}
            data-testid={`service-image-dropzone-${index}`}
          >
            {image ? (
              <div className="relative">
                <img src={image} alt={title} className="w-full h-40 object-cover" />
                <button
                  type="button"
                  onClick={() => { setImage(''); setChanged(true); }}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="h-40 flex flex-col items-center justify-center text-slate-400">
                <Upload className="w-8 h-8 mb-2" />
                <p className="text-xs">Drag & drop image URL</p>
              </div>
            )}
          </div>
          <input
            type="url"
            value={image}
            onChange={(e) => { setImage(e.target.value); setChanged(true); }}
            placeholder="Image URL"
            data-testid={`service-image-url-${index}`}
            className="w-full mt-2 bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-10 px-3 text-sm"
          />
        </div>

        {/* Title + Description */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Service Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setChanged(true); }}
              data-testid={`service-title-${index}`}
              className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => { setDescription(e.target.value); setChanged(true); }}
              rows="3"
              data-testid={`service-desc-${index}`}
              className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 px-4 py-3"
            />
          </div>
          {changed && (
            <button
              onClick={handleSave}
              data-testid={`save-service-${index}`}
              className="bg-orange-500 hover:bg-orange-600 text-white font-oswald px-6 py-2 inline-flex items-center gap-2 transition-all"
            >
              <Save size={16} />
              SAVE CHANGES
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
